import {computed, inject} from "@angular/core";
import {patchState, signalStore, withComputed, withMethods, withState} from "@ngrx/signals";
import {rxMethod} from "@ngrx/signals/rxjs-interop";
import {tapResponse} from "@ngrx/operators";
import {distinctUntilChanged, exhaustMap, of, pipe, tap} from "rxjs";
import {setError, setFulfilled, setLoading, withRequestStatus} from "@shared/store";
import {ElementTypes, WidgetElement, WidgetElementContent, WidgetElementMetadata} from "@entities/widget-element";
import {EditingWidget} from "../model/widgets";
import {
    AnalyticalMetric,
    ChildNodes,
    DeviceMetricNode,
    DeviceNode,
    MenuDeviceMetricNodes,
    MenuDeviceNodes,
    Metric
} from "../model/constructor-menu-nodes";
import {WidgetConstructorMapperService} from "../lib/widget-constructor.mapper.service";
import {WidgetConstructorService} from "../api/widget-constructor.service";
import {CreateElementService} from "../lib/create-element.service";
import {Settings} from "../model/settings";
import {environment} from "@shared/environments";

interface State {
    lastCreatedElementId: number;
    editingWidget: EditingWidget | null;
    editingElement: WidgetElement | null;
    deviceNodes: MenuDeviceNodes;
    deviceMetricNodes: MenuDeviceMetricNodes;
    selectedDeviceNodes: Array<DeviceNode>;
    selectedMetricsNodes: Array<DeviceMetricNode>;
    selectedDeviceNodeId: string | null;
    selectedMetricsNodeId: string | null;
    disabledDeviceNodeId: string | null;
    disabledDeviceMetricsNodeId: string | null;
    selectedMetric: Metric | AnalyticalMetric | null;
    stationDevicesLoaded: boolean;
    stationMetricsLoaded: boolean;
    loadedStationDevicesIds: Record<string, boolean>;
    loadedStationMetricsIds: Record<string, boolean>;
    firstPossiblePosition: { x: number; y: number };
    _datasetSettings: {
        data: Settings;
        isLoading: boolean;
    };
}

const initState = {
    lastCreatedElementId: 0,
    editingWidget: null,
    editingElement: null,
    deviceNodes: {},
    deviceMetricNodes: {},
    selectedDeviceNodes: [],
    selectedMetricsNodes: [],
    selectedDeviceNodeId: null,
    selectedMetricsNodeId: null,
    disabledDeviceNodeId: null,
    disabledDeviceMetricsNodeId: null,
    selectedMetric: null,
    stationDevicesLoaded: false,
    stationMetricsLoaded: false,
    loadedStationDevicesIds: {},
    loadedStationMetricsIds: {},
    firstPossiblePosition: {x: 0, y: 0},
    _datasetSettings: {
        data: {},
        isLoading: false,
    }
};

export const WidgetConstructorStore = signalStore(
    {providedIn: "root"},
    withState<State>(initState),
    withRequestStatus(),
    environment.devTools("widget-constructor"),
    withComputed((store, mapper = inject(WidgetConstructorMapperService)) => ({
        stationDevicesNodes: computed<DeviceNode[]>(() => mapper.mapIdsToDeviceNodes(Object.keys(store.loadedStationDevicesIds()), store.deviceNodes())),
        stationMetricsNodes: computed<DeviceMetricNode[]>(() => mapper.mapIdsToMetricNodes(Object.keys(store.loadedStationMetricsIds()), store.deviceMetricNodes())),
        changeDevicesNodes: computed<DeviceNode[]>(() => {
            const id = store.selectedDeviceNodeId();
            if (id) {
                const node = store.deviceNodes()[id];
                const ids = node.nodes.map(node => node.id);

                return mapper.mapIdsToDeviceNodes(ids, store.deviceNodes());
            } else {
                return mapper.mapIdsToDeviceNodes(Object.keys(store.loadedStationDevicesIds()), store.deviceNodes());
            }
        }),
        changeMetricsNodesDevices: computed<DeviceMetricNode[]>(() => {
            const id = store.selectedMetricsNodeId();
            if (id) {
                const node = store.deviceMetricNodes()[id];
                const ids = node.nodes.map(node => node.id);

                return mapper.mapIdsToMetricNodes(ids, store.deviceMetricNodes());
            }
            {
                return mapper.mapIdsToMetricNodes(Object.keys(store.loadedStationMetricsIds()), store.deviceMetricNodes());
            }
        }),
        changeMetricsNodesMetrics: computed<Metric[]>(() => {
            const id = store.selectedMetricsNodeId();
            if (id) {
                const node = store.deviceMetricNodes()[id];
                return node.metrics;
            } else return [];
        }),
        changeMetricsNodesAnalyticalMetrics: computed<AnalyticalMetric[]>(() => {
            const id = store.selectedMetricsNodeId();
            if (id) {
                const node = store.deviceMetricNodes()[id];
                return node.analyticalMetrics;
            } else return [];
        }),
        datasetSettings: computed(() => {
            if (store.editingElement()?.data.content.elementType === ElementTypes.GAUGE) {
                return store._datasetSettings;
            } else return null;
        }),
    })),
    withMethods((
        store,
        service = inject(WidgetConstructorService),
        mapper = inject(WidgetConstructorMapperService),
        factory = inject(CreateElementService),
    ) => ({
        loadStationDeviceNodes: rxMethod<void>(pipe(
            tap(() => patchState(store, setLoading())),
            exhaustMap(() => {
                if (store.stationDevicesLoaded()) {
                    patchState(store, setFulfilled());
                    return of();
                }

                return service.getDeviceStations().pipe(
                    tapResponse(
                        nodes => patchState(
                            store,
                            updateDeviceNodes(store.deviceNodes(), mapper.mapDtoToMenuDeviceNodes(nodes)),
                            initLoadedStationDevicesIds(mapper.mapDtoTopLevelNodesIds(nodes)),
                            setStationDevicesLoaded(),
                            setFulfilled(),
                        ),
                        error => patchState(store, setError((error as Error).message)),
                    )
                );
            })
        )),
        loadStationMetricNodes: rxMethod<void>(pipe(
            tap(() => patchState(store, setLoading())),
            exhaustMap(() => {
                if (store.stationMetricsLoaded()) {
                    patchState(store, setFulfilled());
                    return of();
                }

                return service.getDeviceMetricStations().pipe(
                    tapResponse(
                        nodes => patchState(
                            store,
                            updateDeviceMetricNodes(store.deviceMetricNodes(), mapper.mapDtoToMenuMetricNodes(nodes)),
                            initLoadedStationMetricsIds(mapper.mapDtoTopLevelNodesIds(nodes)),
                            setStationMetricsLoaded(),
                            setFulfilled(),
                        ),
                        error => patchState(store, setError((error as Error).message)),
                    )
                );
            })
        )),
        loadDeviceNodes: rxMethod<string>(
            pipe(
                distinctUntilChanged(),
                tap(() => patchState(store, setLoading())),
                exhaustMap(id => {
                    if (!(id in store.loadedStationDevicesIds()) || store.loadedStationDevicesIds()[id]) {
                        patchState(store, setFulfilled());
                        return of();
                    }

                    return service.getDeviceNodes(id).pipe(
                        tapResponse(
                            nodes => {
                                const stationChildNodes = mapper.mapDtoToTopLevelChildNodes(nodes);
                                patchState(
                                    store,
                                    updateDeviceNodes(store.deviceNodes(), mapper.mapDtoToMenuDeviceNodes(nodes)),
                                    updateLoadedStationDevicesId(store.loadedStationDevicesIds(), id),
                                    updateSelectedDeviceNodesWithChildren(id, store.selectedDeviceNodes(), stationChildNodes),
                                    setStationDevicesLoaded(),
                                    setFulfilled()
                                );
                                patchState(store, updateStationDeviceNodesWithChildren(id, store.deviceNodes(), stationChildNodes));
                            },
                            error => patchState(store, setError((error as Error).message)),
                        )
                    );
                })
            )
        ),
        loadDeviceMetricNodes: rxMethod<string>(
            pipe(
                distinctUntilChanged(),
                tap(() => patchState(store, setLoading())),
                exhaustMap(id => {
                    if (!(id in store.loadedStationMetricsIds()) || store.loadedStationMetricsIds()[id]) {
                        patchState(store, setFulfilled());
                        return of();
                    }
                    return service.getDeviceMetricNodes(id).pipe(
                        tapResponse(
                            nodes => {
                                const stationChildNodes = mapper.mapDtoToTopLevelChildNodes(nodes);

                                patchState(
                                    store,
                                    updateDeviceMetricNodes(store.deviceMetricNodes(), mapper.mapDtoToMenuMetricNodes(nodes)),
                                    updateLoadedStationMetricsId(store.loadedStationMetricsIds(), id),
                                    updateSelectedMetricsNodesWithChildren(id, store.selectedMetricsNodes(), stationChildNodes),
                                    setStationMetricsLoaded(),
                                    setFulfilled()
                                );

                                patchState(store, updateStationMetricsNodesWithChildren(id, store.deviceMetricNodes(), mapper.mapDtoToTopLevelChildNodes(nodes)));
                            },
                            error => patchState(store, setError((error as Error).message)),
                        )
                    );
                })
            )
        ),
        loadDatasetSettings: rxMethod<string>(pipe(
            distinctUntilChanged(),
            tap(() => patchState(store, setDatasetSettings({}, true))),
            exhaustMap(datasetId => {
                return service.getDatasetSettings(datasetId).pipe(tapResponse(
                    settings => patchState(store, setDatasetSettings(settings, false)),
                    error => patchState(store, setError((error as Error).message), setDatasetSettings({}, false)),
                ));
            })
        )),

        reset() {
            this.resetEditingWidget();
            this.resetEditingElement();
            this.resetSelectedNodeIds();
            this.resetSelectedMetric();
        },
        setEditingWidget(widget: EditingWidget) {
            patchState(store, setEditingWidget(widget));
        },
        resetEditingWidget() {
            patchState(store, resetEditingWidget());
        },
        updateEditingWidgetProps(props: Partial<Pick<EditingWidget, "cols" | "rows" | "name">>) {
            const widget = store.editingWidget()!;
            const newWidget = {...widget, ...props};

            patchState(store, setEditingWidget(newWidget));
        },
        addEditingWidgetElement(type: ElementTypes, {x, y}: { x: number, y: number }) {
            const element = factory.createNewElement(store.lastCreatedElementId(), type, x, y);

            patchState(store, increaseLastCreatedElementId(store.lastCreatedElementId()));
            this.updateEditingWidgetElements([element]);
            this.setEditingElement(element)
        },
        updateEditingWidgetElements(newElements: WidgetElement[]) {
            const widget = store.editingWidget()!;
            const elements = {...widget.elements};

            newElements.forEach(element => {
                elements[element.id] = {
                    id: element.id,
                    h: element.h,
                    w: element.w,
                    x: element.x,
                    y: element.y,
                    data: element.data,
                };
            });

            const newWidget = {
                ...widget,
                elements,
                ...getColsAndRowsFromElements(elements)
            };

            patchState(store, setEditingWidget(newWidget));
        },
        removeEditingWidgetElement(id: string) {
            const widget = store.editingWidget()!;
            const elements = widget.elements;

            delete elements[id];

            if (store.editingElement()?.id === id) {
                this.resetEditingElement();
            }

            patchState(store, setEditingWidget({...widget, elements}));
        },
        updateEditingElementContent(content: Partial<WidgetElementContent>) {
            const element = store.editingElement();
            const updatedContent = {
                ...element!.data.content,
                ...content,
            };
            const newElement: WidgetElement = {
                ...element!,
                data: {
                    ...element!.data,
                    content: updatedContent,
                }
            };

            patchState(store, setEditingElement(newElement));
            this.updateEditingWidgetElements([newElement]);
        },
        updateEditingElementMetadata(metadata: Partial<WidgetElementMetadata>) {
            const element = store.editingElement();
            if (!element) {
                return;
            }
            const updatedMetadata = {
                ...element.data.metadata,
                ...metadata,
            };
            const newElement = {
                ...element,
                data: {
                    ...element.data,
                    metadata: updatedMetadata,
                }
            };

            patchState(store, setEditingElement(newElement));
            this.updateEditingWidgetElements([newElement]);
        },
        removeEditingElementDatasets(ids: string[]) {
            const datasets = store.editingElement()!.data.content.datasets;
            const filteredDatasets = datasets.filter(dataset => !ids.includes(dataset.id));

            this.updateEditingElementContent({datasets: filteredDatasets});
        },
        setEditingElement(element: WidgetElement) {
            patchState(store, setEditingElement(element));
        },
        resetEditingElement() {
            patchState(store, resetEditingElement());
        },
        setSelectedDeviceNodes(nodes: Array<DeviceNode>) {
            patchState(store, setSelectedDeviceNodes(nodes));
        },
        setSelectedMetricsNodes(nodes: Array<DeviceMetricNode>) {
            patchState(store, setSelectedMetricsNodes(nodes));
        },
        addSelectedDeviceNode(node: DeviceNode) {
            const current = store.selectedDeviceNodes();

            patchState(store, setSelectedDeviceNodes([...current, node]));
        },
        replaceSelectedDeviceNodesFrom(index: number, node?: DeviceNode) {
            const current = store.selectedDeviceNodes();
            const nodes = current.slice(0, index);
            const next = [...nodes];

            if (node) next.push(node);

            patchState(store, setSelectedDeviceNodes(next));
        },
        addSelectedMetricsNode(node: DeviceMetricNode) {
            const current = store.selectedMetricsNodes();

            patchState(store, setSelectedMetricsNodes([...current, node]));
        },
        replaceSelectedMetricsNodesFrom(index: number, node?: DeviceMetricNode) {
            const current = store.selectedMetricsNodes();
            const nodes = current.slice(0, index);
            const next = [...nodes];

            if (node) next.push(node);

            patchState(store, setSelectedMetricsNodes(next));
        },
        setSelectedDeviceNodeId(id: string | null) {
            if (id && store.selectedDeviceNodeId() !== id) {
                patchState(store, setSelectedDeviceNodeId(id));
            } else if (!id) {
                patchState(store, resetSelectedDeviceNodeId());
            }
        },
        setSelectedMetricsNodeId(id: string | null) {
            if (id && store.selectedMetricsNodeId() !== id) {
                patchState(store, setSelectedMetricsNodeId(id));
            } else if (!id) {
                patchState(store, resetSelectedMetricsNodeId());
            }
        },
        setDisabledDeviceNodeId(id: string) {
            patchState(store, setDisabledDeviceNodeId(id));
        },
        setDisabledDeviceMetricsNodeId(id: string) {
            patchState(store, setDisabledDeviceMetricsNodeId(id));
        },
        resetSelectedNodeIds() {
            patchState(store, resetSelectedDeviceNodeId(), resetSelectedMetricsNodeId());
        },
        setSelectedMetric(metric: Metric) {
            patchState(store, setSelectedMetric(metric));
        },
        resetSelectedMetric() {
            patchState(store, resetSelectedMetric());
        },
        setFirstPossiblePosition(position: { x: number, y: number }) {
            patchState(store, setFirstPossiblePosition(position));
        },
    })),
);

const increaseLastCreatedElementId = (id: number) => {
    return {lastCreatedElementId: id + 1};
};

const setEditingWidget = (widget: EditingWidget) => {
    return {editingWidget: widget};
};

const resetEditingWidget = () => {
    return {editingWidget: null};
};

const setEditingElement = (el: WidgetElement) => {
    return {editingElement: el};
};

const resetEditingElement = () => {
    return {editingElement: null};
};

const updateDeviceNodes = (prev: MenuDeviceNodes, nodes: MenuDeviceNodes) => {
    return {
        deviceNodes: {
            ...prev,
            ...nodes
        }
    };
};

const updateDeviceMetricNodes = (prev: MenuDeviceMetricNodes, nodes: MenuDeviceMetricNodes) => {
    return {
        deviceMetricNodes: {
            ...prev,
            ...nodes
        }
    };
};

const updateStationDeviceNodesWithChildren = (stationId: string, stationNodes: MenuDeviceNodes, children: ChildNodes) => {
    const stationNode = stationNodes[stationId];

    return {
        deviceNodes: {
            ...stationNodes,
            [stationId]: {
                ...stationNode,
                nodes: children,
            }
        }
    };
};

const updateStationMetricsNodesWithChildren = (stationId: string, stationNodes: MenuDeviceMetricNodes, children: ChildNodes) => {
    const stationNode = stationNodes[stationId];

    return {
        deviceMetricNodes: {
            ...stationNodes,
            [stationId]: {
                ...stationNode,
                nodes: children,
            }
        }
    };
};

const setSelectedDeviceNodes = (nodes: Array<DeviceNode>) => {
    return {selectedDeviceNodes: nodes};
};

const setSelectedMetricsNodes = (nodes: Array<DeviceMetricNode>) => {
    return {selectedMetricsNodes: nodes};
};

const updateSelectedDeviceNodesWithChildren = (id: string, nodes: Array<DeviceNode>, children: ChildNodes) => {
    return {
        selectedDeviceNodes: nodes.map(node => node.id === id ? {...node, nodes: children} : node)
    };
};

const updateSelectedMetricsNodesWithChildren = (id: string, nodes: Array<DeviceMetricNode>, children: ChildNodes) => {
    return {
        selectedMetricsNodes: nodes.map(node => node.id === id ? {...node, nodes: children} : node)
    };
};

const setSelectedDeviceNodeId = (id: string) => {
    return {selectedDeviceNodeId: id};
};

const resetSelectedDeviceNodeId = () => {
    return {selectedDeviceNodeId: null};
};

const setSelectedMetricsNodeId = (id: string) => {
    return {selectedMetricsNodeId: id};
};

const resetSelectedMetricsNodeId = () => {
    return {selectedMetricsNodeId: null};
};

const setDisabledDeviceNodeId = (id: string) => {
    return {disabledDeviceNodeId: id};
};

const setDisabledDeviceMetricsNodeId = (id: string) => {
    return {disabledDeviceMetricsNodeId: id};
};

const setSelectedMetric = (metric: Metric | AnalyticalMetric) => {
    return {selectedMetric: metric};
};

const resetSelectedMetric = () => {
    return {selectedMetric: null};
};

const setStationDevicesLoaded = () => {
    return {stationDevicesLoaded: true};
};

const setStationMetricsLoaded = () => {
    return {stationMetricsLoaded: true};
};

const initLoadedStationDevicesIds = (ids: string[]) => {
    return {
        loadedStationDevicesIds: ids.reduce((acc, id) => {
            acc[id] = false;
            return acc;
        }, {} as Record<string, boolean>),
    };
};

const updateLoadedStationDevicesId = (current: Record<string, boolean>, id: string) => {
    return {
        loadedStationDevicesIds: {
            ...current,
            [id]: true,
        }
    };
};

const initLoadedStationMetricsIds = (ids: string[]) => {
    return {
        loadedStationMetricsIds: ids.reduce((acc, id) => {
            acc[id] = false;
            return acc;
        }, {} as Record<string, boolean>),
    };
};


const updateLoadedStationMetricsId = (current: Record<string, boolean>, id: string) => {
    return {
        loadedStationMetricsIds: {
            ...current,
            [id]: true,
        }
    };
};

const setFirstPossiblePosition = (position: { x: number, y: number }) => {
    return {firstPossiblePosition: position};
};

const setDatasetSettings = (data: Settings, isLoading: boolean) => {
    return {_datasetSettings: {data, isLoading}};
};

const getColsAndRowsFromElements = (elements: Record<string, WidgetElement>) => {
    let maxRow = 0, maxCol = 0;

    for (let id in elements) {
        const bottomSide = elements[id].y + elements[id].h;
        const rightSide = elements[id].x + elements[id].w;

        if (bottomSide > maxRow) {
            maxRow = bottomSide;
        }

        if (rightSide > maxCol) {
            maxCol = rightSide;
        }
    }

    return {cols: maxCol, rows: maxRow};
};
